import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';
import { Plus, Trash2, Search, Loader2, X } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;
const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const uid = () => Math.random().toString(36).slice(2, 9);

// Module-scope so its identity is stable across renders. (Defining this inside
// OrderBuilder remounted the inputs on every keystroke, dropping focus.)
const AddrFields = ({ addr, set, prefix }) => (
  <div className="grid grid-cols-2 gap-2">
    <Input value={addr.first_name || ''} onChange={(e) => set({ ...addr, first_name: e.target.value })} placeholder="First name" className="h-9 rounded-none border-neutral-300 text-sm" data-testid={`${prefix}-first`} />
    <Input value={addr.last_name || ''} onChange={(e) => set({ ...addr, last_name: e.target.value })} placeholder="Last name" className="h-9 rounded-none border-neutral-300 text-sm" data-testid={`${prefix}-last`} />
    <Input value={addr.email || ''} onChange={(e) => set({ ...addr, email: e.target.value })} placeholder="Email" className="h-9 rounded-none border-neutral-300 text-sm" data-testid={`${prefix}-email`} />
    <Input value={addr.phone || ''} onChange={(e) => set({ ...addr, phone: e.target.value })} placeholder="Phone" className="h-9 rounded-none border-neutral-300 text-sm" data-testid={`${prefix}-phone`} />
    <AddressAutocomplete
      apiKey={GMAPS_KEY}
      onSelect={(a) => set({
        ...addr,
        address_1: a.address_1 || addr.address_1,
        city: a.city || addr.city,
        state: a.state || addr.state,
        postcode: a.postcode || addr.postcode,
        country: a.country || addr.country,
      })}
      inputProps={{
        value: addr.address_1 || '',
        onChange: (e) => set({ ...addr, address_1: e.target.value }),
        placeholder: 'Start typing your address…',
        autoComplete: 'off',
        'data-testid': `${prefix}-addr1`,
        className: 'col-span-2 flex h-9 w-full rounded-none border border-neutral-300 bg-white px-3 py-1 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#FF6B00]',
        style: { colorScheme: 'light', color: '#111', backgroundColor: '#fff' },
      }}
    />
    <Input value={addr.address_2 || ''} onChange={(e) => set({ ...addr, address_2: e.target.value })} placeholder="Apt / suite (optional)" className="h-9 rounded-none border-neutral-300 text-sm col-span-2" />
    <Input value={addr.city || ''} onChange={(e) => set({ ...addr, city: e.target.value })} placeholder="City" className="h-9 rounded-none border-neutral-300 text-sm" data-testid={`${prefix}-city`} />
    <Input value={addr.state || ''} onChange={(e) => set({ ...addr, state: e.target.value })} placeholder="State / province" className="h-9 rounded-none border-neutral-300 text-sm" />
    <Input value={addr.postcode || ''} onChange={(e) => set({ ...addr, postcode: e.target.value })} placeholder="Postal / ZIP" className="h-9 rounded-none border-neutral-300 text-sm" />
    <Input value={addr.country || ''} onChange={(e) => set({ ...addr, country: e.target.value })} placeholder="Country" className="h-9 rounded-none border-neutral-300 text-sm" />
  </div>
);

const PIPELINE = [
  ['submitted', 'Submitted'], ['awaiting_payment', 'Confirmed – Awaiting Payment'],
  ['payment_received', 'Payment Received'], ['in_production', 'In Production'],
  ['quality_check', 'Ready to Ship / Quality Check'], ['shipped', 'Shipped'],
  ['completed', 'Completed'], ['cancelled', 'Cancelled'], ['on_hold', 'On Hold – Needs Info'],
];
const EMAIL_STAGES = new Set(['awaiting_payment', 'payment_received', 'in_production', 'shipped', 'completed', 'cancelled', 'on_hold']);

const applyDisc = (base, d) => {
  if (!d) return 0;
  const v = Number(d.value || 0);
  if (v <= 0) return 0;
  return d.type === 'percent' ? Math.round(base * Math.min(v, 100)) / 100 : Math.min(v, base);
};

const blankAddr = { first_name: '', last_name: '', email: '', phone: '', address_1: '', address_2: '', city: '', state: '', postcode: '', country: 'US' };

const DiscountRow = ({ value, onChange, testid }) => {
  const d = value || { type: 'amount', value: '', reason: '' };
  const set = (k, v) => onChange({ ...d, [k]: v });
  return (
    <div className="flex items-center gap-1.5">
      <Select value={d.type || 'amount'} onValueChange={(v) => set('type', v)}>
        <SelectTrigger className="h-8 w-[64px] rounded-none border-neutral-300 text-xs" data-testid={`${testid}-type`}><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="amount">$</SelectItem><SelectItem value="percent">%</SelectItem></SelectContent>
      </Select>
      <Input value={d.value} onChange={(e) => set('value', e.target.value)} placeholder="0" type="number" className="h-8 w-20 rounded-none border-neutral-300 text-xs" data-testid={`${testid}-value`} />
      <Input value={d.reason} onChange={(e) => set('reason', e.target.value)} placeholder="reason (optional)" className="h-8 flex-1 rounded-none border-neutral-300 text-xs" data-testid={`${testid}-reason`} />
    </div>
  );
};

const OrderBuilder = ({ open, onClose, authFetch, initialOrder, onSaved }) => {
  const isEdit = !!initialOrder;
  const [billing, setBilling] = useState(blankAddr);
  const [shipping, setShipping] = useState(blankAddr);
  const [sameShip, setSameShip] = useState(true);
  const [items, setItems] = useState([]);
  const [orderDiscount, setOrderDiscount] = useState({ type: 'amount', value: '', reason: '' });
  const [shippingAmount, setShippingAmount] = useState('');
  const [status, setStatus] = useState('submitted');
  const [notify, setNotify] = useState(false);
  const [customerNote, setCustomerNote] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [saving, setSaving] = useState(false);

  const [catalog, setCatalog] = useState([]);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/api/catalog/bootstrap`).then((r) => r.json()).then((d) => setCatalog(d.products || [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (initialOrder) {
      const b = { ...blankAddr, ...(initialOrder.billing || {}) };
      const s = { ...blankAddr, ...(initialOrder.shipping || {}) };
      setBilling(b); setShipping(s); setSameShip(false);
      setItems((initialOrder.line_items || []).map((li) => ({
        key: uid(), slug: li.slug, name: li.name, variant: li.variant || '', sku: li.sku, image: li.image,
        price: li.price, quantity: li.quantity, custom: !!li.custom,
        discount: li.discount || null,
      })));
      setOrderDiscount(initialOrder.order_discount || { type: 'amount', value: '', reason: '' });
      setShippingAmount(initialOrder.shipping_amount ? String(initialOrder.shipping_amount) : '');
      setCustomerNote(initialOrder.customer_note || '');
      setOrderNumber(initialOrder.order_number || '');
    } else {
      setBilling(blankAddr); setShipping(blankAddr); setSameShip(true); setItems([]);
      setOrderDiscount({ type: 'amount', value: '', reason: '' }); setShippingAmount('');
      setStatus('submitted'); setNotify(false); setCustomerNote(''); setInternalNote(''); setOrderNumber('');
    }
  }, [open, initialOrder]);

  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    return catalog.filter((p) => (p.name || '').toLowerCase().includes(s) || (p.brand || '').toLowerCase().includes(s)).slice(0, 8);
  }, [q, catalog]);

  const addCatalog = (p) => {
    const v = (p.variants && p.variants[0]) || null;
    setItems((prev) => [...prev, {
      key: uid(), slug: p.slug, name: p.name, variant: v?.option || '', sku: p.sku, image: (p.images || [])[0],
      price: v?.price ?? p.price ?? 0, quantity: 1, custom: false, discount: null, _variants: p.variants || [],
    }]);
    setQ('');
  };
  const addCustom = () => setItems((prev) => [...prev, { key: uid(), name: '', price: 0, quantity: 1, custom: true, variant: '', discount: null }]);
  const setItem = (key, patch) => setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  const removeItem = (key) => setItems((prev) => prev.filter((it) => it.key !== key));

  const totals = useMemo(() => {
    let subtotal = 0, lineDisc = 0;
    items.forEach((it) => {
      const sub = Math.round((Number(it.price || 0) * Number(it.quantity || 1)) * 100) / 100;
      subtotal += sub;
      lineDisc += applyDisc(sub, it.discount);
    });
    const afterLine = Math.round((subtotal - lineDisc) * 100) / 100;
    const od = applyDisc(afterLine, orderDiscount);
    const ship = Math.round(Number(shippingAmount || 0) * 100) / 100;
    const total = Math.max(0, Math.round((afterLine - od + ship) * 100) / 100);
    return { subtotal, lineDisc, od, ship, total };
  }, [items, orderDiscount, shippingAmount]);

  const cleanDisc = (d) => (d && Number(d.value || 0) > 0 ? { type: d.type || 'amount', value: Number(d.value), reason: d.reason || '' } : null);

  const save = async () => {
    if (!items.length) { toast({ title: 'Add at least one line item' }); return; }
    if (items.some((it) => !it.name)) { toast({ title: 'Every line needs a name' }); return; }
    setSaving(true);
    try {
      const ship = sameShip ? billing : shipping;
      const payloadItems = items.map((it) => ({
        slug: it.custom ? null : it.slug, name: it.name, variant: it.variant || '', sku: it.sku, image: it.image,
        price: Number(it.price || 0), quantity: Number(it.quantity || 1), custom: !!it.custom, discount: cleanDisc(it.discount),
      }));
      let res;
      if (isEdit) {
        res = await authFetch(`/api/admin/orders/${initialOrder.id}/details`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ billing, shipping: ship, items: payloadItems, order_discount: cleanDisc(orderDiscount), shipping_amount: Number(shippingAmount || 0), customer_note: customerNote, order_number: orderNumber.trim() }),
        });
      } else {
        res = await authFetch('/api/admin/orders', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ billing, shipping: ship, items: payloadItems, order_discount: cleanDisc(orderDiscount), shipping_amount: Number(shippingAmount || 0), status, notify, customer_note: customerNote, internal_note: internalNote, order_number: orderNumber.trim() }),
        });
      }
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Save failed');
      const order = await res.json();
      toast({ title: isEdit ? 'Order updated' : `Order ${order.order_number} created`, description: order._email?.ok ? 'Customer emailed' : undefined });
      onSaved(order);
      onClose();
    } catch (e) {
      toast({ title: 'Could not save', description: String(e.message || e) });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white text-neutral-900 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="order-builder">
        <DialogHeader>
          <DialogTitle className="font-display tracking-[0.12em]">{isEdit ? `EDIT ORDER ${initialOrder.order_number || ''}` : 'CREATE ORDER'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Line items */}
          <section>
            <div className="text-[11px] tracking-[0.2em] text-neutral-500 mb-2">LINE ITEMS</div>
            <div className="relative mb-2">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search catalogue to add a product…" className="h-9 pl-9 rounded-none border-neutral-300 text-sm" data-testid="ob-search" />
              {results.length > 0 && (
                <div className="absolute z-30 left-0 right-0 top-10 bg-white border border-neutral-200 shadow-lg max-h-64 overflow-y-auto">
                  {results.map((p) => (
                    <button key={p.slug} type="button" onClick={() => addCatalog(p)} className="flex items-center justify-between w-full px-3 py-2 text-left hover:bg-neutral-50 text-sm" data-testid={`ob-result-${p.slug}`}>
                      <span className="truncate">{p.name} <span className="text-neutral-400">· {p.brand}</span></span>
                      <span className="text-neutral-500 tabular-nums ml-3">{money(p.price)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              {items.map((it) => (
                <div key={it.key} className="border border-neutral-200 p-3" data-testid={`ob-line-${it.key}`}>
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0 space-y-1.5">
                      {it.custom
                        ? <Input value={it.name} onChange={(e) => setItem(it.key, { name: e.target.value })} placeholder="Custom item name (e.g. Custom fabrication, Deposit)" className="h-9 rounded-none border-neutral-300 text-sm" data-testid={`ob-name-${it.key}`} />
                        : <div className="text-sm font-medium text-neutral-900 truncate">{it.name}</div>}
                      {!it.custom && it._variants && it._variants.length > 0 && (
                        <Select value={it.variant} onValueChange={(v) => { const vv = it._variants.find((x) => x.option === v); setItem(it.key, { variant: v, price: vv?.price ?? it.price }); }}>
                          <SelectTrigger className="h-8 rounded-none border-neutral-300 text-xs w-56"><SelectValue placeholder="Variant" /></SelectTrigger>
                          <SelectContent>{it._variants.map((v) => <SelectItem key={v.option} value={v.option}>{v.option}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                      <div className="flex items-center gap-2">
                        <label className="text-[11px] text-neutral-500">Qty</label>
                        <Input type="number" min="1" value={it.quantity} onChange={(e) => setItem(it.key, { quantity: e.target.value })} className="h-8 w-16 rounded-none border-neutral-300 text-xs" data-testid={`ob-qty-${it.key}`} />
                        <label className="text-[11px] text-neutral-500 ml-2">Unit $</label>
                        <Input type="number" value={it.price} onChange={(e) => setItem(it.key, { price: e.target.value })} className="h-8 w-24 rounded-none border-neutral-300 text-xs" data-testid={`ob-price-${it.key}`} />
                        <span className="text-xs text-neutral-500 ml-auto tabular-nums">{money((it.price || 0) * (it.quantity || 1))}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-neutral-500 w-14">Discount</span>
                        <div className="flex-1"><DiscountRow value={it.discount} onChange={(d) => setItem(it.key, { discount: d })} testid={`ob-linedisc-${it.key}`} /></div>
                      </div>
                    </div>
                    <button type="button" onClick={() => removeItem(it.key)} className="text-red-500 hover:text-red-700 p-1" data-testid={`ob-remove-${it.key}`}><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" onClick={addCustom} className="mt-2 h-8 rounded-none border-neutral-300 text-xs gap-1" data-testid="ob-add-custom"><Plus size={13} /> ADD CUSTOM LINE</Button>
          </section>

          {/* Discounts + shipping + totals */}
          <section className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="text-[11px] tracking-[0.2em] text-neutral-500">ORDER DISCOUNT</div>
              <DiscountRow value={orderDiscount} onChange={setOrderDiscount} testid="ob-orderdisc" />
              <div className="text-[11px] tracking-[0.2em] text-neutral-500 pt-2">SHIPPING CHARGE ($)</div>
              <Input type="number" value={shippingAmount} onChange={(e) => setShippingAmount(e.target.value)} placeholder="0.00" className="h-8 w-32 rounded-none border-neutral-300 text-xs" data-testid="ob-shipping" />
            </div>
            <div className="bg-neutral-50 border border-neutral-200 p-3 text-sm space-y-1" data-testid="ob-totals">
              <div className="flex justify-between text-neutral-600"><span>Subtotal</span><span className="tabular-nums">{money(totals.subtotal)}</span></div>
              {totals.lineDisc > 0 && <div className="flex justify-between text-neutral-600"><span>Line discounts</span><span className="tabular-nums">−{money(totals.lineDisc)}</span></div>}
              {totals.od > 0 && <div className="flex justify-between text-neutral-600"><span>Order discount</span><span className="tabular-nums">−{money(totals.od)}</span></div>}
              {totals.ship > 0 && <div className="flex justify-between text-neutral-600"><span>Shipping</span><span className="tabular-nums">{money(totals.ship)}</span></div>}
              <div className="flex justify-between font-display text-lg text-neutral-900 border-t border-neutral-200 pt-1 mt-1"><span>TOTAL</span><span className="tabular-nums" data-testid="ob-total">{money(totals.total)} <span className="text-xs text-neutral-500">USD</span></span></div>
            </div>
          </section>

          {/* Customer */}
          <section>
            <div className="text-[11px] tracking-[0.2em] text-neutral-500 mb-2">CUSTOMER & BILLING</div>
            <AddrFields addr={billing} set={setBilling} prefix="ob-bill" />
            <label className="flex items-center gap-2 mt-3 text-sm text-neutral-700">
              <Switch checked={sameShip} onCheckedChange={setSameShip} className="data-[state=checked]:bg-[#FF6B00]" data-testid="ob-same-ship" />
              Shipping address same as billing
            </label>
            {!sameShip && (
              <div className="mt-3">
                <div className="text-[11px] tracking-[0.2em] text-neutral-500 mb-2">SHIPPING ADDRESS</div>
                <AddrFields addr={shipping} set={setShipping} prefix="ob-ship" />
              </div>
            )}
          </section>

          {/* Status + notes */}
          <section className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-[11px] tracking-[0.2em] text-neutral-500 mb-2">ORDER NUMBER</div>
              <Input value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder={isEdit ? '' : 'Auto-generated if left blank'} className="h-9 rounded-none border-neutral-300 text-sm" data-testid="ob-order-number" />
              <div className="text-[10px] text-neutral-400 mt-1">
                {isEdit ? 'Change the number for this order. Must be unique.' : 'Leave blank to use the next number from Settings, or type a custom one (e.g. FAD-1050, RUSH-2045).'}
              </div>
            </div>
            <div>
              <div className="text-[11px] tracking-[0.2em] text-neutral-500 mb-2">CUSTOMER NOTE</div>
              <Textarea value={customerNote} onChange={(e) => setCustomerNote(e.target.value)} rows={2} className="rounded-none border-neutral-300 text-sm" data-testid="ob-cust-note" />
            </div>
            {!isEdit && (
              <div>
                <div className="text-[11px] tracking-[0.2em] text-neutral-500 mb-2">STARTING STATUS</div>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="h-9 rounded-none border-neutral-300 text-sm" data-testid="ob-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{PIPELINE.map(([k, l]) => <SelectItem key={k} value={k}>{l}{EMAIL_STAGES.has(k) ? ' ✉' : ''}</SelectItem>)}</SelectContent>
                </Select>
                {EMAIL_STAGES.has(status) && (
                  <label className="flex items-center gap-2 mt-3 text-sm text-neutral-700">
                    <Switch checked={notify} onCheckedChange={setNotify} className="data-[state=checked]:bg-[#FF6B00]" data-testid="ob-notify" />
                    Email the customer now
                  </label>
                )}
                <div className="text-[11px] tracking-[0.2em] text-neutral-500 mt-3 mb-2">INTERNAL NOTE</div>
                <Textarea value={internalNote} onChange={(e) => setInternalNote(e.target.value)} rows={2} className="rounded-none border-neutral-300 text-sm" data-testid="ob-internal-note" />
              </div>
            )}
          </section>

          <div className="flex justify-end gap-2 border-t border-neutral-200 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-neutral-300"><X size={14} className="mr-1" /> Cancel</Button>
            <Button type="button" onClick={save} disabled={saving} className="rounded-none bg-black hover:bg-[#FF6B00] font-display tracking-[0.12em] text-xs gap-2" data-testid="ob-save">
              {saving ? <Loader2 size={14} className="animate-spin" /> : (isEdit ? 'SAVE CHANGES' : 'CREATE ORDER')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrderBuilder;
